import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Api } from '../api/api';
import getValidTelegram from '../utils/getValidTelegram';
import { useProfileStore } from './profileStore';

interface User {
  id: string;
  email: string;
  user_name: string;
  first_name?: string;
  last_name?: string;
  telegram?: string;
  vk_account?: string;
  show_telegram: boolean;
  show_vk: boolean;
}

// Интерфейс для данных, которые будут храниться в localStorage
interface PersistedState {
  token: string | null;
  tokenRefreshedAt: string | null; // Время последнего обновления токена
}

// Интерфейс для полного состояния авторизации
interface AuthState extends PersistedState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitializing: boolean; // Флаг для начальной проверки токена
  hasInitialized: boolean; // Флаг что инициализация уже была выполнена
  error: string | null;
  
  // Actions
  login: (login: string, password: string) => Promise<void>;
  register: (email: string, password: string, nickname: string, telegram?: string) => Promise<{
    success: boolean;
    requiresTelegramVerification: boolean;
    telegramLink?: string;
    telegramToken?: string;
    error?: string;
  }>;
  logout: () => void;
  clearErrors: () => void;
  validateTokenOnInit: () => Promise<void>;
  checkAndRefreshTokenIfNeeded: () => Promise<boolean>; // Новый метод для обновления при наступлении нового дня
}

// Создаем хранилище для токена, которое будет сохраняться в localStorage
const useTokenStore = create<PersistedState>()(
  persist(
    (_set) => ({
      token: null,
      tokenRefreshedAt: null,
    }),
    {
      name: 'auth-token-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// Функция для преобразования данных пользователя из API в формат User
const mapUserInfoToUser = (userInfo: any): User => ({
  id: '1', // API не возвращает id, используем фиксированное значение
  email: userInfo.email || '',
  user_name: userInfo.user_name || 'Пользователь',
  first_name: userInfo.first_name || undefined,
  last_name: userInfo.last_name || undefined,
  telegram: userInfo.telegram_account || undefined,
  vk_account: userInfo.vk_account || undefined,
  show_telegram: Boolean(userInfo.show_telegram),
  show_vk: Boolean(userInfo.show_vk)
});

// Вспомогательная функция для сохранения токена с меткой времени
const saveTokenWithTimestamp = (token: string) => {
  const now = new Date().toISOString();
  useTokenStore.setState({ token, tokenRefreshedAt: now });
  return now;
};

// Функция проверки, нужно ли обновлять токен (наступил ли новый день)
const shouldRefreshToken = (tokenRefreshedAt: string | null): boolean => {
  if (!tokenRefreshedAt) return true; // Если времени нет, обновляем
  
  const lastRefresh = new Date(tokenRefreshedAt);
  const now = new Date();
  
  // Сравниваем даты без учета времени
  const lastRefreshDate = new Date(lastRefresh.getFullYear(), lastRefresh.getMonth(), lastRefresh.getDate());
  const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Обновляем если наступил новый день
  return currentDate.getTime() > lastRefreshDate.getTime();
};

// Основное хранилище состояния авторизации
export const useAuthStore = create<AuthState>()((set, get) => {
  // Получаем начальные значения токена и времени обновления из localStorage
  const { token: initialToken, tokenRefreshedAt: initialTokenRefreshedAt } = useTokenStore.getState();
  
  return {
    user: null,
    token: initialToken,
    tokenRefreshedAt: initialTokenRefreshedAt,
    isAuthenticated: false, // НЕ доверяем токену из localStorage до проверки
    isLoading: false,
    isInitializing: !!initialToken, // Проверяем токен только если он есть
    hasInitialized: false, // Флаг что инициализация еще не была выполнена
    error: null,

    login: async (login, password) => {
      set({ isLoading: true, error: null });
      try {
        const api = new Api();
        const response = await api.login({
          telegram: getValidTelegram(login),
          password
        });
        
        // Проверяем есть ли ошибка в ответе
        if (response.error) {
          set({ error: response.error, isLoading: false });
          return;
        }
        
        // Проверяем есть ли токен
        if (!response.token) {
          set({ error: 'Токен не получен от сервера', isLoading: false });
          return;
        }
        
        // Сохраняем токен с меткой времени
        const tokenRefreshedAt = saveTokenWithTimestamp(response.token);
        
        // Обновляем токен, но НЕ устанавливаем isAuthenticated пока не получим данные пользователя
        set({ token: response.token, tokenRefreshedAt });
        
        // Создаем новый экземпляр Api с обновленным токеном
        const apiWithToken = new Api();
        // Обновляем токен в API явно
        apiWithToken.updateToken();
        
        // Получаем информацию о пользователе
        const userInfo = await apiWithToken.getUserInfo();
        
        // Устанавливаем isAuthenticated только после полного завершения процесса
        set({ 
          user: mapUserInfoToUser(userInfo),
          token: response.token,
          tokenRefreshedAt,
          isAuthenticated: true, 
          isLoading: false 
        });
        
        // Принудительно перезагружаем данные нового пользователя
        try {
          const { useInventoryStore } = require('./inventoryStore');
          const useStatisticsStore = require('./statisticsStore').default;
          
          // Сбрасываем состояния и перезагружаем данные
          useInventoryStore.getState().resetInventoryState();
          useStatisticsStore.getState().resetStatisticsState();
          
          // Загружаем новые данные в фоне (не ждем результата)
          useStatisticsStore.getState().fetchStatistics().catch(() => {});
          useInventoryStore.getState().fetchInventory(0, 5).catch(() => {});
        } catch (error) {
          // Игнорируем ошибки перезагрузки данных
        }
      } catch (error) {
        // При любой ошибке очищаем токен
        useTokenStore.setState({ token: null, tokenRefreshedAt: null });
        set({ token: null, tokenRefreshedAt: null, isAuthenticated: false });
        let errorMessage = 'Ошибка авторизации. Проверьте логин и пароль.';
        
        if (error instanceof Error) {
          const errorText = error.message.toLowerCase();
          
          if (errorText.includes('401') || errorText.includes('403')) {
            errorMessage = 'Неверный логин или пароль. Пожалуйста, проверьте введенные данные.';
          } else if (errorText.includes('404')) {
            errorMessage = 'Пользователь не найден. Проверьте логин или зарегистрируйтесь.';
          } else if (errorText.includes('network') || errorText.includes('failed to fetch')) {
            errorMessage = 'Ошибка сети. Пожалуйста, проверьте подключение к интернету.';
          } else if (errorText.includes('timeout')) {
            errorMessage = 'Превышено время ожидания ответа от сервера. Попробуйте позже.';
          }
        }
        
        set({ error: errorMessage, isLoading: false });
      }
    },

    register: async (email, password, nickname, telegram) => {
      set({ isLoading: true, error: null });
      try {
        const api = new Api();
        const response = await api.signUp({
          user_name: nickname,
          email,
          telegram: getValidTelegram(telegram || ''),
          password
        });
        
        // Проверяем, требуется ли верификация через Telegram
        if (response.link) {
          // Если есть ссылка на бота Telegram, возвращаем информацию о необходимости верификации
          set({ isLoading: false });
          return {
            success: true,
            requiresTelegramVerification: true,
            telegramLink: response.link,
            telegramToken: response.token || ''
          };
        }
        
        // После успешной регистрации выполняем вход с новым экземпляром Api
        const loginApi = new Api();
        const loginResponse = await loginApi.login({
          telegram: getValidTelegram(telegram || email),
          password
        });
        
        // Сохраняем токен с меткой времени
        const tokenRefreshedAt = saveTokenWithTimestamp(loginResponse.token);
        
        // Создаем новый экземпляр Api с обновленным токеном
        const apiWithToken = new Api();
        
        // Получаем информацию о пользователе
        const userInfo = await apiWithToken.getUserInfo();
        
        set({ 
          user: mapUserInfoToUser(userInfo),
          token: loginResponse.token,
          tokenRefreshedAt,
          isAuthenticated: true, 
          isLoading: false 
        });
        
        // Принудительно перезагружаем данные нового пользователя
        try {
          const { useInventoryStore } = require('./inventoryStore');
          const useStatisticsStore = require('./statisticsStore').default;
          
          // Сбрасываем состояния и перезагружаем данные
          useInventoryStore.getState().resetInventoryState();
          useStatisticsStore.getState().resetStatisticsState();
          
          // Загружаем новые данные в фоне (не ждем результата)
          useStatisticsStore.getState().fetchStatistics().catch(() => {});
          useInventoryStore.getState().fetchInventory(0, 5).catch(() => {});
        } catch (error) {
          // Игнорируем ошибки перезагрузки данных
        }
        
        return { success: true, requiresTelegramVerification: false };
      } catch (error) {
        let errorMessage = 'Ошибка регистрации. Попробуйте другие данные.';
        
        if (error instanceof Error) {
          const errorText = error.message.toLowerCase();
          
          if (errorText.includes('already exists') || errorText.includes('уже существует') || errorText.includes('занят')) {
            errorMessage = 'Пользователь с таким email или telegram уже зарегистрирован.';
          } else if (errorText.includes('invalid') || errorText.includes('некорректный')) {
            if (errorText.includes('email')) {
              errorMessage = 'Указан некорректный email адрес.';
            } else if (errorText.includes('password')) {
              errorMessage = 'Пароль не соответствует требованиям безопасности.';
            } else {
              errorMessage = 'Проверьте правильность введенных данных.';
            }
          } else if (errorText.includes('network') || errorText.includes('failed to fetch')) {
            errorMessage = 'Ошибка сети. Пожалуйста, проверьте подключение к интернету.';
          } else if (errorText.includes('timeout')) {
            errorMessage = 'Превышено время ожидания ответа от сервера. Попробуйте позже.';
          }
        }
        
        set({ error: errorMessage, isLoading: false });
        return { 
          success: false, 
          requiresTelegramVerification: false,
          error: errorMessage 
        };
      }
    },

    logout: () => {
      // Очищаем токен и время обновления в localStorage
      useTokenStore.setState({ token: null, tokenRefreshedAt: null });
      
      // Сбрасываем данные всех хранилищ
      useProfileStore.getState().resetProfileState();
      
      // Импортируем хранилища напрямую чтобы избежать циклических зависимостей
      const { useInventoryStore } = require('./inventoryStore');
      const useStatisticsStore = require('./statisticsStore').default;
      
      useInventoryStore.getState().resetInventoryState();
      useStatisticsStore.getState().resetStatisticsState();
      
      set({ 
        user: null, 
        token: null, 
        tokenRefreshedAt: null,
        isAuthenticated: false 
      });
    },

    clearErrors: () => {
      set({ error: null });
    },

    validateTokenOnInit: async () => {
      const { token, hasInitialized } = get();
      
      // Если уже была инициализация, не выполняем повторно
      if (hasInitialized) {
        return;
      }
      
      if (!token) {
        // Нет токена - завершаем инициализацию
        set({ isInitializing: false, hasInitialized: true });
        return;
      }
      
      try {
        // Создаем API экземпляр с существующим токеном
        const api = new Api();
        api.updateToken(); // Убеждаемся что токен обновлен
        
        // Проверяем, нужно ли обновить токен (если наступил новый день)
        const refreshed = await get().checkAndRefreshTokenIfNeeded();
        
        // Если токен был обновлен, обновляем API экземпляр
        if (refreshed) {
          api.updateToken();
        }
        
        // Пытаемся получить данные пользователя для проверки валидности токена
        const userInfo = await api.getUserInfo();
        
        // Если токен валидный, устанавливаем пользователя как авторизованного
        set({ 
          user: mapUserInfoToUser(userInfo),
          isAuthenticated: true,
          isInitializing: false,
          hasInitialized: true
        });
        
        // Принудительно сбрасываем кэш данных при валидации токена
        try {
          const { useInventoryStore } = require('./inventoryStore');
          const useStatisticsStore = require('./statisticsStore').default;
          
          // Сбрасываем состояния
          useInventoryStore.getState().resetInventoryState();
          useStatisticsStore.getState().resetStatisticsState();
        } catch (error) {
          // Игнорируем ошибки сброса состояний
        }
      } catch (error) {
        // Токен невалидный - очищаем его
        useTokenStore.setState({ token: null, tokenRefreshedAt: null });
        set({ 
          token: null, 
          tokenRefreshedAt: null,
          isAuthenticated: false,
          user: null,
          isInitializing: false,
          hasInitialized: true
        });
      }
    },

    checkAndRefreshTokenIfNeeded: async () => {
      const { token, tokenRefreshedAt, isAuthenticated } = get();
      
      // Если нет токена или пользователь не авторизован, не обновляем
      if (!token || !isAuthenticated) {
        return false;
      }
      
      // Проверяем, нужно ли обновлять токен
      if (!shouldRefreshToken(tokenRefreshedAt)) {
        return false; // Обновление не требуется
      }
      
      try {
        // Создаем API экземпляр и пытаемся обновить токен
        const api = new Api();
        const refreshResult = await api.refreshToken();
        
        if (refreshResult && !refreshResult.error && refreshResult.token) {
          // Сохраняем новый токен с меткой времени
          const newTokenRefreshedAt = saveTokenWithTimestamp(refreshResult.token);
          
          // Обновляем состояние
          set({ 
            token: refreshResult.token,
            tokenRefreshedAt: newTokenRefreshedAt
          });
          
          return true; // Токен был обновлен
        } else {
          return false;
        }
      } catch (error) {
        return false;
      }
    }
  };
});
